/**
 * 1.6.0 the example AI description flows (plan C2; Mikael, 2026-10-25), one per provider: Claude (Messages API, image
 * blocks, structured JSON output), OpenAI (the ChatGPT API, image inputs, JSON schema output), Gemini
 * (generateContent, inline image parts, response schema; optionally the proxy video for a whole-asset summary)
 * and OpenRouter (OpenAI-compatible chat completions; the same flow works against a local Ollama or vLLM server by
 * changing the URL). Each comes with its webhook endpoint template (URL, the API key header's name), the graph (For
 * each shot → Call webhook with bodyJson and a signed frame link → a check of the answer → Update segment from the
 * answer) and a note on where the media goes and the provider's data-retention setting.
 *
 * The shapes follow each provider's documentation (2026-10-25): Claude's Messages API with model claude-opus-5-5, no
 * sampling parameters (temperature, top_p and top_k answer 400), output_config.format with a JSON schema and an
 * explicit output_config.effort (Opus 5.5 defaults to medium), the refusal stop reason checked, and server-side
 * fallbacks (`fallbacks: "default"` with the `anthropic-beta: server-side-fallback-2026-07-01` header); OpenAI's chat
 * completions with image_url parts and response_format json_schema (strict); Gemini's generateContent with
 * inline_data parts and generationConfig.responseMimeType/responseSchema; OpenRouter's /api/v1/chat/completions
 * (OpenAI-compatible, response_format json_schema, provider.data_collection).
 *
 * Every flow writes its answer with Call webhook's `json` output (the answer's text read as JSON), always as a
 * suggestion. 1.6.2: the flow follows the chosen log layer (aiTemplateGraph): a text layer gets a description
 * ({{nodes.ai.output.json.description}} as text), a vocabulary layer one of its options (an enum of the labels,
 * {{nodes.ai.output.json.value}} as a value) and a tags layer a list ({{nodes.ai.output.json.tags}} as values). Stream A's file (docs/dev/mcp-1.6.0-build.md): the
 * Workflows page's "New from template" and the Webhooks page's "Add from a template" read this list.
 */
import type { AiFlowProvider } from './ai-describe.ts';
import type { AutomationGraph, Condition } from './automation.ts';
import type { StratumKind } from './strata.ts';

export interface AiEndpointTemplate {
  name: string;
  url: string;
  method: 'POST';
  /** The header that carries the API key (its value is typed by the administrator, never stored here). */
  apiKeyHeader: string;
  /** A prefix the key's value takes, e.g. "Bearer ". */
  apiKeyPrefix: string;
  /** Other fixed headers (anthropic-version, …). */
  headers: Record<string, string>;
  description: string;
}

export interface AiFlowTemplate {
  id: string;
  provider: AiFlowProvider;
  /** The provider's short name, for a flow's default name ("Shot type with Claude"). */
  providerName: string;
  name: string;
  description: string;
  endpoint: AiEndpointTemplate;
  /** The request body for an answer spec (the instructions, the JSON schema of the answer and its name). */
  body: (spec: AiAnswerSpec) => unknown;
  /** The "Answered?" check; `answerPath` is the answer's field the layer is written from (nodes.ai.output.json.<field>). */
  check: (answerPath: string) => Condition;
  /**
   * The flow for the starter "Content description" text layer (TEMPLATE_STRATUM_ID); its call_webhook step names the
   * endpoint as TEMPLATE_ENDPOINT_ID. aiTemplateGraph makes the flow for a chosen endpoint and layer.
   */
  graph: AutomationGraph;
  /** Where the media goes, and the provider's retention setting to check. */
  dataNote: string;
  /** Other notes for the administrator (fallbacks, a local server, a whole-asset option). */
  notes?: string[];
}

/** The placeholder endpoint id in a template's graph. */
export const TEMPLATE_ENDPOINT_ID = '00000000-0000-4000-8000-0000000000a1';
/** The log layer a template's `graph` writes to (the starter "Content description" layer); chosen when the flow is made. */
export const TEMPLATE_STRATUM_ID = 's_content';

/** The log layer a flow writes to (a StratumDTO has these). */
export interface AiTemplateLayer {
  id: string;
  label: string;
  kind: StratumKind;
  options?: ReadonlyArray<{ value: string; label: string }>;
}

/** The subset of JSON Schema the answers use (each provider's body puts it in its own dialect). */
export interface JsonSchema {
  type: 'object' | 'string' | 'array';
  description?: string;
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  enum?: readonly string[];
  required?: readonly string[];
  additionalProperties?: boolean;
}

/** What the flow asks the model for: the system instructions and the answer's JSON schema. */
export interface AiAnswerSpec {
  instructions: string;
  schema: JsonSchema;
  /** A name for the schema (OpenAI's json_schema.name: letters, digits, _ and -). */
  schemaName: string;
}

/** A layer's options at most (StratumInput's limit), so a vocabulary's enum is never longer. */
export const AI_TEMPLATE_MAX_OPTIONS = 200;

/** What every flow asks for on a text layer: one description per shot, structured. */
export const AI_DESCRIPTION_SCHEMA = {
  type: 'object',
  properties: {
    description: { type: 'string', description: 'One or two plain sentences about what is seen in the shot' },
    tags: { type: 'array', items: { type: 'string' }, description: 'A few words for people, places and things seen' },
  },
  required: ['description', 'tags'],
  additionalProperties: false,
} as const;

const INSTRUCTIONS = 'You describe shots of video for a media archive. Write one or two plain, factual sentences about what is seen: '
  + 'people (by role, never guessed names), place, action, shot size and any on-screen text. Use the dialogue only as context. '
  + 'Answer in English with the JSON the schema asks for.';
const SHOT_PROMPT = 'Shot {{item.number}} of {{item.count}} of “{{asset.title}}”, {{item.startTimecode}}–{{item.endTimecode}} ({{item.durationSeconds}} s). Dialogue in the shot: {{item.text}}';

/** A layer's option labels (unique, at most AI_TEMPLATE_MAX_OPTIONS). */
const optionLabels = (layer: AiTemplateLayer) => [...new Set((layer.options ?? []).map((o) => o.label.trim()).filter(Boolean))].slice(0, AI_TEMPLATE_MAX_OPTIONS);

const cap = (s: string, n = 80) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** What the flow asks for on a layer, the answer's field it writes from, and what Update segment writes. */
function answerFor(layer: AiTemplateLayer): { spec: AiAnswerSpec; field: string; text: string; values: string[]; askLabel: string; writeLabel: string } {
  if (layer.kind === 'vocabulary') {
    const labels = optionLabels(layer);
    return {
      field: 'value', text: '', values: ['{{nodes.ai.output.json.value}}'],
      askLabel: cap(`Classify the shot: ${layer.label}`), writeLabel: cap(`Write ${layer.label}`),
      spec: {
        schemaName: 'shot_value',
        instructions: `You classify shots of video for a media archive into one option of the “${layer.label}” log layer. `
          + `${layer.label}: choose the one that fits, exactly one of these, written as listed: ${labels.join('; ')}. `
          + 'Use the dialogue only as context. Answer with the JSON the schema asks for.',
        schema: {
          type: 'object',
          properties: { value: { type: 'string', enum: labels, description: `The one ${layer.label} option that fits the shot` } },
          required: ['value'],
          additionalProperties: false,
        },
      },
    };
  }
  if (layer.kind === 'tags') {
    return {
      field: 'tags', text: '', values: ['{{nodes.ai.output.json.tags}}'],
      askLabel: cap(`List ${layer.label} in the shot`), writeLabel: cap(`Write ${layer.label}`),
      spec: {
        schemaName: 'shot_tags',
        instructions: `You tag shots of video for a media archive for the “${layer.label}” log layer. `
          + `List short tags for ${layer.label} in the shot, a word or two each, in English. `
          + 'Name a person by role (reporter, farmer, child), never by a guessed name. '
          + 'Leave the list empty when nothing fits. Use the dialogue only as context. Answer with the JSON the schema asks for.',
        schema: {
          type: 'object',
          properties: { tags: { type: 'array', items: { type: 'string' }, description: `Short tags for ${layer.label} in the shot` } },
          required: ['tags'],
          additionalProperties: false,
        },
      },
    };
  }
  return {
    field: 'description', text: '{{nodes.ai.output.json.description}}', values: [],
    askLabel: 'Describe the shot', writeLabel: 'Write the description',
    spec: { instructions: INSTRUCTIONS, schema: AI_DESCRIPTION_SCHEMA as unknown as JsonSchema, schemaName: 'shot_description' },
  };
}

/** Why a flow can't write the layer (null when it can): a vocabulary needs options to choose from. */
export function aiTemplateLayerProblem(layer: AiTemplateLayer): string | null {
  if (layer.kind === 'vocabulary' && !optionLabels(layer).length) {
    return `“${layer.label}” has no options to choose from yet: add some in Administration → Log layers, or choose another layer.`;
  }
  return null;
}

/** What a flow made for the layer does, in a line ("Picks one of Wide, Medium, … for each shot"). */
export function aiTemplateSummary(layer: AiTemplateLayer, shown = 6): string {
  if (layer.kind === 'vocabulary') {
    const labels = optionLabels(layer);
    if (!labels.length) return `Picks one of the “${layer.label}” options for each shot`;
    return `Picks one of ${labels.slice(0, shown).join(', ')}${labels.length > shown ? `, … (${labels.length} options)` : ''} for each shot`;
  }
  if (layer.kind === 'tags') return `Lists ${layer.label} for each shot`;
  return 'Writes a description of each shot';
}

/** A flow's default name: the template's own for a text layer, else "<layer> with <provider>". */
export function aiTemplateName(t: Pick<AiFlowTemplate, 'name' | 'providerName'>, layer: AiTemplateLayer): string {
  return layer.kind === 'text' ? t.name : `${layer.label} with ${t.providerName}`;
}

const pos = (x: number, y = 120) => ({ x, y });

/**
 * The template's flow for an endpoint and a log layer: For each shot → Call webhook (the AI, asked for the layer's
 * answer) → the answer's check → Update segment (a suggestion). A text layer gets a description as text; a vocabulary
 * layer one of its options (an enum of the labels; the API maps a label to the option's value); a tags layer a list
 * (rendered comma separated and split into values). An empty text leaves an existing suggestion's text as it is.
 */
export function aiTemplateGraph(t: Pick<AiFlowTemplate, 'body' | 'check'>, layer: AiTemplateLayer, endpointId: string): AutomationGraph {
  const a = answerFor(layer);
  return {
    schemaVersion: 1,
    nodes: [
      { id: 'trigger', type: 'trigger', label: 'Run on an asset', trigger: { type: 'manual', subject: 'asset' }, position: pos(0) },
      { id: 'each', type: 'for_each_shot', label: 'For each shot', concurrency: 2, maxItems: 500, onItemFailure: 'continue', position: pos(300) },
      {
        id: 'ai', type: 'call_webhook', label: a.askLabel, endpointId, path: '', payload: {},
        bodyJson: JSON.stringify(t.body(a.spec), null, 2), requestTimeoutSeconds: 90, mediaLinkTtlSeconds: 3600, retries: 2, position: pos(600, 260),
      },
      { id: 'answered', type: 'condition', label: 'Answered?', all: [t.check(`nodes.ai.output.json.${a.field}`)], retries: 0, position: pos(900, 260) },
      { id: 'write', type: 'update_segment', label: a.writeLabel, stratumId: layer.id, text: a.text, values: a.values, position: pos(1200, 260) },
    ],
    edges: [
      { from: 'trigger', port: 'next', to: 'each' },
      { from: 'each', port: 'each', to: 'ai' },
      { from: 'ai', port: 'next', to: 'answered' },
      { from: 'answered', port: 'true', to: 'write' },
    ],
  } as AutomationGraph;
}

/** The starter text layer the templates' `graph` writes to. */
const CONTENT_LAYER: AiTemplateLayer = { id: TEMPLATE_STRATUM_ID, label: 'Content description', kind: 'text', options: [] };

const claudeBody = (spec: AiAnswerSpec) => ({
  model: 'claude-opus-5-5',
  max_tokens: 1024,
  // Fallbacks: a request Claude's safety classifiers decline is re-run on Anthropic's recommended model (the
  // anthropic-beta header server-side-fallback-2026-07-01 is on the endpoint).
  fallbacks: 'default',
  output_config: { effort: 'low', format: { type: 'json_schema', schema: spec.schema } },
  system: spec.instructions,
  messages: [{
    role: 'user',
    content: [
      { type: 'image', source: { type: 'url', url: '{{shotFrameUrl item.index}}' } },
      { type: 'text', text: SHOT_PROMPT },
    ],
  }],
});

const openAiBody = (model: string, extra: Record<string, unknown> = {}) => (spec: AiAnswerSpec) => ({
  model,
  messages: [
    { role: 'system', content: spec.instructions },
    {
      role: 'user',
      content: [
        { type: 'text', text: SHOT_PROMPT },
        { type: 'image_url', image_url: { url: '{{shotFrameUrl item.index}}', detail: 'low' } },
      ],
    },
  ],
  response_format: { type: 'json_schema', json_schema: { name: spec.schemaName, strict: true, schema: spec.schema } },
  ...extra,
});

/** A JSON schema in Gemini's responseSchema dialect (upper-case types and enums; no descriptions or additionalProperties). */
export function geminiSchema(s: JsonSchema): Record<string, unknown> {
  const out: Record<string, unknown> = { type: s.type.toUpperCase() };
  if (s.properties) out.properties = Object.fromEntries(Object.entries(s.properties).map(([k, v]) => [k, geminiSchema(v)]));
  if (s.items) out.items = geminiSchema(s.items);
  if (s.enum) out.enum = [...s.enum];
  if (s.required) out.required = [...s.required];
  return out;
}

const geminiBody = (spec: AiAnswerSpec) => ({
  systemInstruction: { parts: [{ text: spec.instructions }] },
  contents: [{
    role: 'user',
    parts: [
      { inline_data: { mime_type: 'image/jpeg', data: '{{base64Frame item.middleSeconds}}' } },
      { text: SHOT_PROMPT },
    ],
  }],
  generationConfig: {
    responseMimeType: 'application/json',
    responseSchema: geminiSchema(spec.schema),
  },
});

/** A template with its text-layer graph. */
const withGraph = (t: Omit<AiFlowTemplate, 'graph'>): AiFlowTemplate => ({ ...t, graph: aiTemplateGraph(t, CONTENT_LAYER, TEMPLATE_ENDPOINT_ID) });

export const AI_FLOW_TEMPLATES: AiFlowTemplate[] = [
  withGraph({
    id: 'describe-shots-claude', provider: 'claude', providerName: 'Claude', name: 'Describe shots with Claude',
    description: 'For each shot, sends its middle frame (a signed link) and its dialogue to Claude’s Messages API (claude-opus-5-5, structured JSON output) and writes the answer to the chosen log layer as a suggestion.',
    endpoint: {
      name: 'Claude (Anthropic)', url: 'https://api.anthropic.com/v1/messages', method: 'POST', apiKeyHeader: 'x-api-key', apiKeyPrefix: '',
      headers: { 'anthropic-version': '2026-10-21', 'anthropic-beta': 'server-side-fallback-2026-07-01' },
      description: 'Claude Messages API. The API key comes from the Claude Console (console.anthropic.com → API keys).',
    },
    body: claudeBody,
    check: () => ({ path: 'nodes.ai.output.response.stop_reason', op: 'not_equals', value: 'refusal' }),
    dataNote: 'Each shot’s frame is fetched by Anthropic from PRISM’s signed link (valid one hour, logged), with the shot’s dialogue and the asset’s title. '
      + 'Anthropic does not train on API data by default and keeps API inputs and outputs for a limited time (30 days by default); a zero data retention agreement shortens that. Check your organisation’s settings in the Claude Console.',
    notes: [
      'Model claude-opus-5-5 at effort low (Opus 5.5 defaults to medium; raise it for harder material). Sampling parameters (temperature, top_p, top_k) are not sent: the API refuses them.',
      'A declined request (stop_reason refusal) is re-run on Anthropic’s recommended fallback model (fallbacks: "default", header anthropic-beta: server-side-fallback-2026-07-01); if it is still declined, the “Answered?” step leaves that shot alone.',
      'For base64 instead of a link, use {"type": "base64", "media_type": "image/jpeg", "data": "{{base64Frame item.middleSeconds}}"} as the image source.',
    ],
  }),
  withGraph({
    id: 'describe-shots-openai', provider: 'openai', providerName: 'OpenAI', name: 'Describe shots with OpenAI',
    description: 'For each shot, sends its middle frame (a signed link) and its dialogue to OpenAI’s chat completions with a JSON schema answer, and writes the answer to the chosen log layer as a suggestion.',
    endpoint: {
      name: 'OpenAI (ChatGPT API)', url: 'https://api.openai.com/v1/chat/completions', method: 'POST', apiKeyHeader: 'Authorization', apiKeyPrefix: 'Bearer ',
      headers: {}, description: 'OpenAI chat completions. The API key comes from platform.openai.com → API keys.',
    },
    body: openAiBody('gpt-6-astra'),
    check: () => ({ path: 'nodes.ai.output.response.choices.0.message.refusal', op: 'not_exists' }),
    dataNote: 'Each shot’s frame is fetched by OpenAI from PRISM’s signed link (valid one hour, logged), with the dialogue and the title. '
      + 'OpenAI does not train on API data by default and keeps abuse-monitoring logs for up to 30 days; zero data retention is available on request. Check the project’s data controls on platform.openai.com.',
    notes: ['Change the model in the body to one your project may use. "detail": "low" keeps each image cheap; use "high" for small details.'],
  }),
  withGraph({
    id: 'describe-shots-gemini', provider: 'gemini', providerName: 'Gemini', name: 'Describe shots with Gemini',
    description: 'For each shot, sends its middle frame (inline, base64) and its dialogue to Gemini’s generateContent with a response schema, and writes the answer to the chosen log layer as a suggestion.',
    endpoint: {
      name: 'Google Gemini', url: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent', method: 'POST',
      apiKeyHeader: 'x-goog-api-key', apiKeyPrefix: '', headers: {}, description: 'Gemini API generateContent. The API key comes from Google AI Studio (aistudio.google.com → API keys).',
    },
    body: geminiBody,
    check: () => ({ path: 'nodes.ai.output.response.candidates.0.finishReason', op: 'equals', value: 'STOP' }),
    dataNote: 'Each shot’s frame goes inline in the request (base64, made from the proxy and logged), with the dialogue and the title. '
      + 'On the paid tier Google does not use prompts to improve its products and keeps them for a limited time for abuse monitoring; on the free tier they may be used. Check the project’s billing tier in Google AI Studio.',
    notes: [
      'Whole-asset summary: Gemini can also take the proxy video itself. Add a second Call webhook after the For each step with a part {"file_data": {"mime_type": "video/mp4", "file_uri": "{{proxyUrl}}"}} and the text “Summarise this video in three sentences”, then Update metadata as a suggestion from {{nodes.<step>.output.json.description}}. Longer videos may need Gemini’s Files API first.',
      'The model is part of the endpoint’s URL (models/<model>:generateContent): change it there.',
    ],
  }),
  withGraph({
    id: 'describe-shots-openrouter', provider: 'openrouter', providerName: 'OpenRouter', name: 'Describe shots with OpenRouter (or a local model)',
    description: 'For each shot, sends its middle frame and its dialogue to an OpenAI-compatible chat completions API (OpenRouter: one key for many models) and writes the answer to the chosen log layer as a suggestion. The same flow works against a local Ollama or vLLM server.',
    endpoint: {
      name: 'OpenRouter', url: 'https://openrouter.ai/api/v1/chat/completions', method: 'POST', apiKeyHeader: 'Authorization', apiKeyPrefix: 'Bearer ',
      headers: { 'X-Title': 'PRISM' }, description: 'OpenRouter chat completions (OpenAI-compatible). The API key comes from openrouter.ai → Keys.',
    },
    body: openAiBody('google/gemini-3.8-flash', { provider: { data_collection: 'deny' } }),
    check: (answerPath) => ({ path: answerPath, op: 'exists' }),
    dataNote: 'Each shot’s frame is fetched from PRISM’s signed link (valid one hour, logged) by OpenRouter or the model provider it routes to. '
      + '"provider": {"data_collection": "deny"} in the body routes only to providers that do not store or train on the data. A local server keeps everything on your network.',
    notes: [
      'Local model: point the endpoint at your server, e.g. http://ollama.local:11434/v1/chat/completions (Ollama) or http://vllm.local:8000/v1/chat/completions (vLLM), with no key, add its host to PRISM_WEBHOOK_ALLOW_HOSTS, and use a vision model (e.g. "qwen2.5vl"); remove the "provider" field. A local server must be able to fetch PRISM’s links, or use {{base64Frame item.middleSeconds}} as a data URL: "data:image/jpeg;base64,{{base64Frame item.middleSeconds}}".',
      'Change the model to any vision model OpenRouter lists.',
    ],
  }),
];
